import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_URL, buildKioskAuthHeaders } from './api';
import { operationalScope } from './workstationIdentity';
import WorkstationSettings from './WorkstationSettings';
import './KdsAccessGate.css';

export class KdsAccessError extends Error {
  constructor() { super('Accès cuisine à vérifier.'); this.kdsAccessDenied = true; }
}
const accessFailure = (response, payload) => [401, 402, 403].includes(response.status) || /^(KDS_|WORKSTATION_)/.test(payload?.code || '');
const validDevice = device => device && Number.isSafeInteger(device.id) && device.id > 0 && typeof device.name === 'string'
  && (device.productionZoneId === null || (Number.isSafeInteger(device.productionZoneId) && device.productionZoneId > 0))
  && Number.isSafeInteger(device.configurationRevision) && device.configurationRevision > 0;
const deviceKey = device => device ? `${device.id}:${device.productionZoneId}:${device.configurationRevision}` : 'legacy';
function newSessionSecret() {
  const bytes = new Uint8Array(32); window.crypto.getRandomValues(bytes);
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
}

export default function KdsAccessGate({ children }) {
  const [session] = useState(newSessionSecret);
  const [scope] = useState(operationalScope);
  const [identity] = useState(() => { const value = buildKioskAuthHeaders(); return [value.get('Authorization'), value.get('X-BE-Device')]; });
  const [state, setState] = useState({ status: 'checking', canBind: false, canTakeover: false });
  const active = useRef(false), generation = useRef(0), controlSequence = useRef(0);
  const permission = useRef(null), metadata = useRef({ canBind: false, canTakeover: false });
  const renewal = useRef(null), expiry = useRef(null), controller = useRef(null);
  const openRef = useRef(null);
  const clearTimers = useCallback(() => { clearTimeout(renewal.current); clearTimeout(expiry.current); }, []);
  const currentScope = useCallback(() => {
    const value = buildKioskAuthHeaders();
    return Boolean(scope) && operationalScope() === scope && value.get('Authorization') === identity[0] && value.get('X-BE-Device') === identity[1];
  }, [identity, scope]);
  const suspend = useCallback((status, message) => {
    generation.current += 1; controlSequence.current += 1; permission.current = null; clearTimers();
    if (active.current) setState({ ...metadata.current, status, message });
  }, [clearTimers]);
  const headers = useCallback(() => {
    const value = buildKioskAuthHeaders(); value.set('X-BE-KDS-Session', session); return value;
  }, [session]);

  const open = useCallback(async (takeover = false, renew = false) => {
    if (!active.current) return;
    if (!currentScope()) { suspend('denied', 'Compte modifié ou poste changé. Rechargez la page cuisine.'); return; }
    const sequence = ++controlSequence.current;
    controller.current?.abort(); controller.current = new AbortController();
    const isCurrent = () => active.current && sequence === controlSequence.current && currentScope();
    if (!renew) {
      generation.current += 1; permission.current = null; clearTimers();
      setState({ ...metadata.current, status: 'checking' });
    }
    try {
      let access = metadata.current;
      if (!renew) {
        const response = await fetch(`${API_URL}/api/kds/access`, { headers: headers(), cache: 'no-store', signal: controller.current.signal });
        const payload = await response.json().catch(() => null);
        if (!isCurrent()) return;
        if (!response.ok) {
          if (typeof payload?.canBind === 'boolean') metadata.current = { ...metadata.current, canBind: payload.canBind, canTakeover: payload.canTakeover === true };
          suspend(/^WORKSTATION_/.test(payload?.code || '') ? 'binding' : 'denied', 'L’accès cuisine est indisponible pour ce compte ou ce poste.'); return;
        }
        if (!['legacy', 'licensed'].includes(payload?.mode) || typeof payload.canBind !== 'boolean'
          || typeof payload.canTakeover !== 'boolean' || typeof payload.bindingRequired !== 'boolean') throw new Error('invalid-access');
        access = payload; metadata.current = payload;
        if (access.mode === 'legacy') {
          permission.current = { mode: 'legacy', device: null, generation: generation.current };
          setState({ ...access, status: 'ready', generation: generation.current }); return;
        }
        if (document.visibilityState === 'hidden') { suspend('paused', 'Cet écran est en pause. Vérifiez son autorisation pour reprendre.'); return; }
        if (access.bindingRequired || !validDevice(access.device)) {
          suspend('binding', 'Associez cet écran à un poste cuisine pour continuer.'); return;
        }
      }
      const requestHeaders = headers(); requestHeaders.set('Content-Type', 'application/json');
      const requestedAt = Date.now();
      const response = await fetch(`${API_URL}/api/kds/session`, { method: 'POST', headers: requestHeaders,
        body: JSON.stringify({ takeover }), cache: 'no-store', signal: controller.current.signal });
      const payload = await response.json().catch(() => null);
      if (!isCurrent()) return;
      if (!response.ok) {
        if (typeof payload?.canBind === 'boolean') metadata.current = { ...metadata.current, canBind: payload.canBind, canTakeover: payload.canTakeover === true };
        if (response.status === 409 && payload?.code === 'KDS_SESSION_IN_USE') {
          suspend('conflict', 'Ce poste cuisine est utilisé sur un autre écran.'); return;
        }
        suspend(/^WORKSTATION_/.test(payload?.code || '') ? 'binding' : 'denied', 'L’autorisation de cet écran doit être vérifiée.'); return;
      }
      if (payload?.mode !== 'licensed' || !validDevice(payload.device) || !Number.isFinite(payload.expiresInMs)
        || payload.expiresInMs <= 0 || payload.expiresInMs > 90000 || !Number.isFinite(payload.renewAfterMs)
        || payload.renewAfterMs <= 0 || payload.renewAfterMs >= payload.expiresInMs) throw new Error('invalid-session');
      if (permission.current && deviceKey(permission.current.device) !== deviceKey(payload.device)) generation.current += 1;
      const remaining = payload.expiresInMs - (Date.now() - requestedAt);
      if (remaining <= 0) { suspend('expired', 'L’autorisation de cet écran a expiré. Vérifiez la connexion pour reprendre.'); return; }
      clearTimers();
      permission.current = { mode: 'licensed', device: payload.device, generation: generation.current, deadline: Date.now() + remaining };
      setState({ ...metadata.current, device: payload.device, status: 'ready', mode: 'licensed', generation: generation.current });
      renewal.current = setTimeout(() => openRef.current(false, true), Math.min(payload.renewAfterMs, remaining));
      expiry.current = setTimeout(() => suspend('expired', 'L’autorisation de cet écran a expiré. Vérifiez la connexion pour reprendre.'), remaining);
    } catch (error) {
      if (!isCurrent() || error.name === 'AbortError') return;
      suspend('offline', 'Connexion au serveur indisponible. Les actions déjà enregistrées sont conservées.');
    }
  }, [clearTimers, currentScope, headers, suspend]);
  openRef.current = open;
  useEffect(() => {
    active.current = true; open();
    const onReturn = () => {
      if (document.visibilityState === 'hidden') {
        if (permission.current?.mode === 'licensed' || metadata.current.mode === 'licensed') suspend('paused', 'Cet écran est en pause. Vérifiez son autorisation pour reprendre.');
      } else if (metadata.current.mode === 'licensed') openRef.current();
    };
    const onIdentityChange = () => { if (!currentScope()) suspend('denied', 'Compte modifié ou poste changé. Rechargez la page cuisine.'); };
    const onOffline = () => { if (permission.current?.mode === 'licensed') suspend('offline', 'Connexion au serveur interrompue. Les actions déjà enregistrées sont conservées.'); };
    window.addEventListener('offline', onOffline);
    window.addEventListener('storage', onIdentityChange);
    window.addEventListener('focus', onIdentityChange);
    document.addEventListener('visibilitychange', onReturn);
    return () => { active.current = false; generation.current += 1; controlSequence.current += 1; permission.current = null; clearTimers(); controller.current?.abort(); document.removeEventListener('visibilitychange', onReturn); window.removeEventListener('offline', onOffline); window.removeEventListener('storage', onIdentityChange); window.removeEventListener('focus', onIdentityChange); };
  }, [clearTimers, currentScope, open, suspend]);

  const request = useCallback(async (path, options = {}, expectedGeneration) => {
    const before = permission.current;
    const permitted = () => active.current && currentScope() && before && permission.current
      && before.generation === expectedGeneration && permission.current.generation === expectedGeneration
      && (permission.current.mode === 'legacy' || Date.now() < permission.current.deadline);
    const ensurePermission = () => {
      if (permitted()) return;
      if (active.current && !currentScope()) suspend('denied', 'Compte modifié ou poste changé. Rechargez la page cuisine.');
      throw new KdsAccessError();
    };
    ensurePermission();
    if (!/^\/api\/kds\/(orders(?:\/\d+\/(?:status|kitchen-zone-status|kitchen-undo))?|settings|production-zones)$/.test(path)) throw new KdsAccessError();
    const requestHeaders = buildKioskAuthHeaders(options.headers);
    if (before.mode === 'licensed') requestHeaders.set('X-BE-KDS-Session', session);
    try {
      const response = await fetch(`${API_URL}${path}`, { ...options, headers: requestHeaders });
      ensurePermission();
      if (!response.ok) {
        const payload = await (typeof response.clone === 'function' ? response.clone() : response).json().catch(() => null);
        ensurePermission();
        if (accessFailure(response, payload)) {
          if (typeof payload?.canBind === 'boolean') metadata.current = { ...metadata.current, canBind: payload.canBind, canTakeover: payload.canTakeover === true };
          suspend(/^WORKSTATION_/.test(payload?.code || '') ? 'binding' : 'denied', 'L’autorisation de cet écran a changé. Vérifiez-la pour reprendre.'); throw new KdsAccessError();
        }
      }
      return { ok: response.ok, status: response.status, json: async () => {
        try {
          const data = await response.json(); ensurePermission(); return data;
        } catch (error) {
          if (before.mode === 'licensed' && permitted()) suspend('offline', 'Connexion au serveur interrompue. Les actions déjà enregistrées sont conservées.');
          if (before.mode === 'licensed' || !permitted()) throw new KdsAccessError();
          throw error;
        }
      } };
    } catch (error) {
      if (error.kdsAccessDenied || !permitted()) throw new KdsAccessError();
      if (before.mode === 'licensed') {
        suspend('offline', 'Connexion au serveur interrompue. Les actions déjà enregistrées sont conservées.'); throw new KdsAccessError();
      }
      throw error;
    }
  }, [currentScope, session, suspend]);
  const boundRequest = useMemo(() => (path, options) => request(path, options, state.generation), [request, state.generation]);
  if (state.status === 'ready') return children({ request: boundRequest, mode: state.mode, device: state.device, generation: state.generation });
  return <main className="kds-access-page"><section className="kds-access-card">
    <span>Cuisine</span><h1>Votre écran de préparation</h1>
    {state.status === 'checking' ? <p role="status">Vérification de l’accès cuisine…</p> : <>
      <p role="alert">{state.message}</p>
      {state.status === 'conflict' && state.canTakeover && <button type="button" onClick={() => open(true)}>Utiliser ce poste sur cet écran</button>}
      {state.canBind && state.status === 'binding' && <WorkstationSettings canBind allowedDeviceTypes={['kitchen']}
        bindingMessage="Un administrateur doit associer cet écran à un poste cuisine."
        onAssociated={() => window.location.reload()} />}
      {state.status === 'binding' && !state.canBind && <p>Un administrateur doit associer cet écran au poste cuisine.</p>}
      <button type="button" className="kds-access-retry" onClick={() => open()}>Vérifier à nouveau</button>
    </>}
    <a href="/admin/select?product=becaisse">Retour à mon espace</a>
  </section></main>;
}
